// externals
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';

// components
import type { DatePickerEventValue } from '@interstate/components/DatePicker';
import { DatePicker } from '@interstate/components/DatePicker';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputSize } from '@interstate/components/TextInput';

// selectors
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as dealLifeCycleActionCreators from '@makemydeal/dr-dash-store/actions/dealLifeCycleActionCreators';
import * as dealLifecycleSelectors from '@makemydeal/dr-dash-store/selectors/dealLifecycleSelectors';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { dealLockHooks } from '../hooks';

// styles
import { StyledDateLabel } from './DealLifecycleModal.style';

// consts/enums
import { DealLifeCycleStatus, DATE_FORMAT, SALE, SALE_DATE, SOLD, SOLD_DATE_REQUIRED_ERROR } from '../constants';
import { DealTabs } from '@makemydeal/dr-shared-types';

interface SaleDateFieldProps {
    id: string;
    value: string;
    onChange: (e: InterstateOnChangeEvent<DatePickerEventValue>, type: string) => void;
    dealStatusValue: string;
    showStyledLabel?: boolean;
    size?: TextInputSize;
}

export const SaleDateField: React.FC<SaleDateFieldProps> = ({
    id,
    value,
    onChange,
    dealStatusValue,
    showStyledLabel = false,
    size = 'medium'
}) => {
    const dispatch = useDispatch();
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isLocked = dealLockHooks.useIsDealLocked();
    const maxAllowedFutureMonthsInDateField = useSelector(configSelectors.getMaxAllowedFutureMonthsInDateField);
    const dealLifeCycle = useSelector(dealLifecycleSelectors.getDealLifecycle);
    const currentSaleDate = dealLifeCycle.soldDate || '';
    const currentDate = new Date();
    const maxAllowedDate = new Date(currentDate.setMonth(currentDate.getMonth() + maxAllowedFutureMonthsInDateField));
    const oneYearAgoFromNow = new Date(currentDate.setFullYear(currentDate.getFullYear() - 1));
    const isSoldDateRequired = useMemo(() => dealStatusValue === SOLD, [dealStatusValue, value]);
    const soldDateHasError = isSoldDateRequired && value === '';
    const isParentSummaryTab = id.includes(DealTabs.SUMMARY);

    const isFieldEmpty = (fieldValue: string): boolean => {
        return fieldValue === '';
    };

    const getDateFieldDisabledState = useMemo(
        () =>
            (dealStatusValue === DealLifeCycleStatus.IN_PROGRESS || dealStatusValue === DealLifeCycleStatus.LOST) &&
            isFieldEmpty(currentSaleDate || ''),
        [dealStatusValue, currentSaleDate]
    );

    const handleOpenModal = useCallback(() => {
        dispatch(dealLifeCycleActionCreators.showDealLifeCycleModal(true));
    }, [dispatch]);

    useEffect(() => {
        if (isParentSummaryTab && isDealScreenRedDotErrorsEnabled) {
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.SUMMARY, id, soldDateHasError));
        }
    }, [soldDateHasError, isDealScreenRedDotErrorsEnabled, dispatch, id, isParentSummaryTab]);

    return (
        <>
            {showStyledLabel && (
                <StyledDateLabel data-testid={`${id}-label`} onClick={handleOpenModal}>
                    {SALE_DATE}
                </StyledDateLabel>
            )}
            <DatePicker
                id={id}
                data-testid={id}
                label={SALE_DATE}
                dateFormat={DATE_FORMAT}
                value={value ? new Date(value) : null}
                onChange={(e) => onChange(e, SALE)}
                disabled={isLocked || getDateFieldDisabledState}
                required={isSoldDateRequired}
                hasError={soldDateHasError}
                locale="en-US"
                maxDate={maxAllowedDate}
                minDate={oneYearAgoFromNow}
                errorMessage={soldDateHasError ? SOLD_DATE_REQUIRED_ERROR : undefined}
                displayLabel={!showStyledLabel}
                size={size}
            />
        </>
    );
};
